import { Button } from "@/src/components/buttons";
import { Card } from "@/src/components/cards";
import { ColorCircle } from "@/src/components/colorCircle";
import { ColorsList } from "@/src/components/colorsListPicker";
import { openDrawer } from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";

import {
  Field,
  FieldOption,
  InputsType,
  ScoreInterval,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import { getUserId, uid, updateOrPush } from "@/src/utils/utils";
import { useState } from "react";

export function FieldScoreIntervals({
  field,
  scoreIntervals,
  onScoreIntervalsChange,
}: {
  field: Field;
  scoreIntervals: (ScoreInterval | null | undefined)[] | undefined | null;
  onScoreIntervalsChange:
    | ((newValue: (ScoreInterval | null | undefined)[]) => void)
    | null
    | undefined;
}) {
  if (onScoreIntervalsChange == null) return null;
  if (
    !(
      ["select", "chips-multiple", "chips", "select-multiple", "boolean"] as InputsType[]
    ).includes(field.type)
  )
    return (
      <ScoreIntervalsList
        currentField={field}
        scoreIntervals={scoreIntervals}
        onScoreIntervalsChange={onScoreIntervalsChange}
      />
    );

  if (field.type == "boolean") {
    field.options = [
      {
        id: "options:true",
        name: "Oui",
        value: "true",
        creator: getUserId(),
        description: "",
      },
      {
        id: "options:false",
        name: "Non",
        value: "false",
        creator: getUserId(),
        description: "",
      },
    ];
  }

  return (
    <div className="gray c1 ">
      <div className="bold">Points</div>
      {field.options?.map((option, index) => {
        return (
          <div key={uid()} className="c4 acenter " onClick={() => {}}>
            <div className="triple">{option?.name}</div>
            <div>
              {
                <OptionScoreIntervalPicker
                  option={option}
                  field={field!}
                  scoreIntervals={scoreIntervals}
                  onScoreIntervalsChange={(newScoreIntervals) => {
                    console.log("the new score intervals", newScoreIntervals);
                    onScoreIntervalsChange?.(newScoreIntervals);
                  }}
                />
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ScoreIntervalsList({
  gridCount,
  currentField,
  scoreIntervals,
  onScoreIntervalsChange,
}: {
  gridCount?: number;
  currentField: Field | null | undefined;
  scoreIntervals: (ScoreInterval | null | undefined)[] | undefined | null;
  onScoreIntervalsChange:
    | ((newValue: (ScoreInterval | null | undefined)[]) => void)
    | null
    | undefined;
}) {
  const [reload, setReload] = useState(0);
  if (onScoreIntervalsChange == null) return null;

  async function openIntervalDialog(
    interval: ScoreInterval | null | undefined,
    index: number
  ) {
    if (!interval) return;
    await openDrawer({
      title: "Interval",
      content: (
        <MinMaxInterValForm
          current={interval}
          onChange={(newValue) => {
            interval = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        if (!interval) return;
        scoreIntervals ||= [];
        if (index == -1) {
          scoreIntervals.push(interval);
        } else {
          scoreIntervals[index] = interval;
        }

        onScoreIntervalsChange?.(scoreIntervals);
      },
    });
  }

  return (
    <div className="c1 gap10">
      <div className="">Intervals </div>
      <div className={`grid gap10`}>
        {scoreIntervals?.map((interval, index) => {
          if (!interval) return null;
          if (currentField && interval.field?.id != currentField.id)
            return null;

          return (
            <Card
              key={uid()}
              color="red"
              onEdit={async () => {
                await openIntervalDialog(interval, index);
              }}
              onDelete={async () => {
                scoreIntervals ||= [];
                scoreIntervals.splice(index, 1);
                onScoreIntervalsChange?.(scoreIntervals);
              }}
            >
              <div className="cauto-1 gap5 acenter">
                <ColorCircle color={interval.color}></ColorCircle>
                <div>
                  <div>
                    {interval.min} - {interval.max}
                  </div>
                  <div className="gray">{interval.interpretation}</div>
                </div>
              </div>
            </Card>
          );
        })}
        <Button
          type={"tertiary"}
          onClick={async () => {
            let interval: ScoreInterval = {
              field: currentField,
              color: "green",
              type: "minMax",
              value: "",
              points: 0,
              interpretation: "",
              min: 0,
              max: 0,
              creator: USER?.get(),
            };
            await openIntervalDialog(interval, -1);
            setReload(reload + 1);
          }}
        >
          +
        </Button>
      </div>
    </div>
  );
}

function OptionScoreIntervalPicker({
  option,
  field,
  scoreIntervals,
  onScoreIntervalsChange,
}: {
  option: FieldOption | null | undefined;
  field: Field;
  scoreIntervals: (ScoreInterval | null | undefined)[] | undefined | null;
  onScoreIntervalsChange:
    | ((newValue: (ScoreInterval | null | undefined)[]) => void)
    | null
    | undefined;
}) {
  if (onScoreIntervalsChange == null) return null;

  let interval = scoreIntervals?.find(
    (interval) =>
      interval?.field?.id == field.id && interval?.value == option?.id
  ) || {
    field,
    color: "green",
    type: "points",

    value: option?.id,
    points: 0,
    min: 0,
    max: 0,
    interpretation: "",
    creator: USER?.get(),
  };
  return (
    <Input
      name=""
      type="number"
      isHorizontal={false}
      value={interval.points}
      onChange={(newValue) => {
        interval.points = newValue;

        scoreIntervals = updateOrPush(
          scoreIntervals,
          interval,
          (interval) =>
            interval?.field?.id == field.id && interval?.value == option?.id
        );
        onScoreIntervalsChange(scoreIntervals);
      }}
    />
  );
}

function MinMaxInterValForm({
  current,
  onChange,
}: {
  current: ScoreInterval;
  onChange: (newValue: ScoreInterval) => void;
}) {
  let [interval, setInterval] = useState(current);
  function setNewValue(newValue: ScoreInterval) {
    setInterval(newValue);
    onChange(newValue);
  }

  return (
    <div className="grid gap10 c1">
      <Input
        name={"Min"}
        type={"number"}
        value={interval.min}
        onChange={(newValue) => {
          setNewValue({ ...interval, min: newValue });
        }}
      />
      <Input
        name={"Max"}
        type={"number"}
        value={interval.max}
        onChange={(newValue) => {
          setNewValue({ ...interval, max: newValue });
        }}
      />

      <Input
        name={"Points"}
        type={"number"}
        value={interval.points}
        onChange={(newValue) => {
          setNewValue({ ...interval, points: newValue });
        }}
      />
      <Input
        name={"Interprétation"}
        type={"text"}
        value={interval.interpretation}
        onChange={(newValue) => {
          setNewValue({ ...interval, interpretation: newValue });
        }}
      />
      <ColorsList
        label={"Couleur"}
        value={interval.color}
        onChange={(newValue) => {
          setNewValue({ ...interval, color: newValue });
        }}
      />
    </div>
  );
}
